// src/pages/OnboardingWizard.js
import React, { useMemo, useRef, useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import pb from '../lib/pocketbase';
import './onboarding.css';

import confetti from 'canvas-confetti';

// Version 2.1 - Enhanced Recap UI grid fixed

function Stepper({ step, total, onStepClick }) {
  return (
    <div className="wizard-steps">
      {Array.from({ length: total }).map((_, i) => (
        <button
          key={i}
          type="button"
          className={`wizard-dot wizard-dot-btn ${step === i + 1 ? 'active' : step > i + 1 ? 'done' : ''}`}
          onClick={() => onStepClick?.(i + 1)}
          aria-current={step === i + 1 ? 'step' : undefined}
        >
          {i + 1}
        </button>
      ))}
    </div>
  );
}

export default function OnboardingWizard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const referralId = searchParams.get('ref');
  const total = 4;
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false); // Success state for popup
  const [status, setStatus] = useState('entreprise'); // entreprise | association | particulier
  const [client, setClient] = useState({ firstName: '', lastName: '', company: '', email: '', phone: '', address: '', postalCode: '', city: '', password: '', confirm: '' });
  const [needs, setNeeds] = useState({ channels: [], projectName: '', budget: '', objectives: '', budgetMin: 1000, budgetMax: 3000, budgetType: 'one_time' });
  const [notes, setNotes] = useState('');

  // Celebration effect
  useEffect(() => {
    if (success) {
      const duration = 5 * 1000;
      const animationEnd = Date.now() + duration;
      const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 10000 };

      const randomInRange = (min, max) => Math.random() * (max - min) + min;

      const interval = setInterval(() => {
        const timeLeft = animationEnd - Date.now();
        if (timeLeft <= 0) return clearInterval(interval);

        const particleCount = 50 * (timeLeft / duration);
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 } }));
        confetti(Object.assign({}, defaults, { particleCount, origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 } }));
      }, 250);

      // Auto redirect after 7 seconds to let user enjoy the success
      const timeout = setTimeout(() => {
        navigate('/mon-compte');
      }, 7000);

      return () => {
        clearInterval(interval);
        clearTimeout(timeout);
      };
    }
  }, [success, navigate]);

  const [draftCustomerId, setDraftCustomerId] = useState(() => localStorage.getItem('onb_customer_id') || '');
  const savingRef = useRef(false);
  const [errors, setErrors] = useState({});
  const CHANNEL_TIPS = {
    'Site Web': 'Votre vitrine ou application en ligne',
    'SEO': 'Optimiser votre positionnement Google',
    'SEA/Ads': 'Publicités payantes Google/Facebook',
    'Réseaux sociaux': 'Stratégie et contenus social media',
    'Emailing': 'Newsletters et automatisations',
    'Branding': 'Identité de marque et design',
    'Contenu': 'Articles, vidéos, visuels',
    'Application mobile': 'App iOS/Android, PWA, expérience mobile',
    'Analytics & Data': 'Balises, dashboards, suivi KPIs',
    'Marketplace': 'Gestion marketplaces (Amazon, Etsy…) ',
    'Autre': 'Un autre levier (à préciser)'
  };

  const [editField, setEditField] = useState('');
  const [editValue, setEditValue] = useState('');
  const [recapEditMode, setRecapEditMode] = useState(false);
  const startEdit = (field, initial) => {
    if (!recapEditMode) return;
    setEditField(field); setEditValue(initial ?? '');
  };
  const saveEdit = async () => {
    if (!editField) return;
    if (editField === 'name') {
      const parts = String(editValue).trim().split(/\s+/);
      setClient(c => ({ ...c, firstName: parts[0] || '', lastName: parts.slice(1).join(' ') || '' }));
    } else if (editField === 'company') {
      setClient(c => ({ ...c, company: editValue }));
    } else if (editField === 'email') {
      setClient(c => ({ ...c, email: editValue }));
    } else if (editField === 'phone') {
      setClient(c => ({ ...c, phone: editValue }));
    } else if (editField === 'address') {
      setClient(c => ({ ...c, address: editValue }));
    } else if (editField === 'projectName') {
      setNeeds(n => ({ ...n, projectName: editValue }));
    } else if (editField === 'objectives') {
      setNeeds(n => ({ ...n, objectives: editValue }));
    }
    setEditField('');
    try { await saveCustomerDraft(); } catch { }
  };
  const cancelEdit = () => { setEditField(''); setEditValue(''); };
  const ICONS = {
    entreprise: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 21h18" />
        <path d="M6 21V7h12v14" />
        <path d="M9 10h2M13 10h2M9 14h2M13 14h2M9 18h2M13 18h2" />
      </svg>
    ),
    association: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M10 13a5 5 0 0 1 0-7l1-1a5 5 0 0 1 7 7l-1 1" />
        <path d="M14 11a5 5 0 0 1 0 7l-1 1a5 5 0 1 1-7-7l1-1" />
      </svg>
    ),
    particulier: () => (
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8z" />
        <path d="M5 21a7 7 0 0 1 14 0" />
      </svg>
    )
  };
  const [isDark, setIsDark] = useState(() => {
    try {
      const saved = localStorage.getItem('theme');
      if (saved === 'dark') return true;
    } catch { }
    return typeof document !== 'undefined' ? document.body.classList.contains('dark') : false;
  });

  const toggleTheme = () => {
    const makeDark = !document.body.classList.contains('dark');
    document.body.classList.toggle('dark', makeDark);
    try { localStorage.setItem('theme', makeDark ? 'dark' : 'light'); } catch { }
    setIsDark(makeDark);
  };

  useEffect(() => {
    if (referralId) {
      localStorage.setItem('onb_ref_id', referralId);
    }
  }, [referralId]);

  useEffect(() => {
    const validateDraft = async () => {
      const id = localStorage.getItem('onb_customer_id');
      if (!id) return;
      try { await pb.collection('customers').getOne(id); }
      catch { localStorage.removeItem('onb_customer_id'); setDraftCustomerId(''); }
    };
    validateDraft();
  }, []);

  const stepValid = useMemo(() => {
    if (step === 1) return ['entreprise', 'association', 'particulier'].includes(status);
    if (step === 2) {
      const emailOk = !!client.email && /.+@.+\..+/.test(client.email);
      const nameOk = !!client.firstName && !!client.lastName;
      const confirmOk = (client.password || '') === (client.confirm || '');
      const pwOk = (client.password || '').length >= 8;
      return emailOk && nameOk && pwOk && confirmOk;
    }
    return true;
  }, [step, status, client]);

  const canNext = step < total && (step === 2 ? true : stepValid);

  const next = async () => {
    if (step === 2) {
      const emailOk = !!client.email && /.+@.+\..+/.test(client.email);
      const nameOk = !!client.firstName && !!client.lastName;
      const pwOk = (client.password || '').length >= 8;
      const confirmOk = client.password === client.confirm;
      const phoneOk = !!client.phone;
      const addressOk = !!client.address;
      const postalOk = !!client.postalCode;
      const cityOk = !!client.city;
      const e = {};
      if (!client.firstName) e.firstName = 'Prénom requis';
      if (!client.lastName) e.lastName = 'Nom requis';
      if (!emailOk) e.email = 'Email invalide';
      if (!pwOk) e.password = '8 caractères minimum';
      if (!confirmOk) e.confirm = 'Les mots de passe ne correspondent pas';
      if (!phoneOk) e.phone = 'Téléphone requis';
      if (!addressOk) e.address = 'Adresse requise';
      if (!postalOk) e.postalCode = 'Code postal requis';
      if (!cityOk) e.city = 'Ville requise';
      if (Object.keys(e).length) { setErrors(e); return; }
      setErrors({});
      try { await createCustomerAfterStep2(); } catch { alert("Impossible de créer la fiche client pour le moment."); return; }
    }
    setStep((s) => Math.min(total, s + 1));
  };
  const prev = async () => {
    try { await saveCustomerDraft(); } catch { }
    setStep((s) => Math.max(1, s - 1));
  };

  const fullName = (fn, ln) => [fn, ln].filter(Boolean).join(' ').trim();

  const createCustomerAfterStep2 = async () => {
    if (draftCustomerId || savingRef.current) return;
    const payload = {
      name: fullName(client.firstName, client.lastName),
      email: client.email,
      phone: client.phone || '',
      address: [client.address, client.postalCode, client.city].filter(Boolean).join(' '),
      company: status !== 'particulier' ? (client.company || '') : '',
      notes: buildNotes(),
      commercial_id: referralId || localStorage.getItem('onb_ref_id') || undefined
    };
    try {
      savingRef.current = true;
      const rec = await pb.collection('customers').create(payload);
      setDraftCustomerId(rec.id);
      localStorage.setItem('onb_customer_id', rec.id);
    } finally {
      savingRef.current = false;
    }
  };

  const saveCustomerDraft = async () => {
    if (savingRef.current || !draftCustomerId) return;
    const payload = {
      name: fullName(client.firstName, client.lastName) || 'Client',
      email: client.email || '',
      phone: client.phone || '',
      address: [client.address, client.postalCode, client.city].filter(Boolean).join(' '),
      company: status !== 'particulier' ? (client.company || '') : '',
      notes: buildNotes(),
      commercial_id: referralId || localStorage.getItem('onb_ref_id') || undefined
    };
    try {
      savingRef.current = true;
      await pb.collection('customers').update(draftCustomerId, payload);
    } catch { }
    finally { savingRef.current = false; }
  };

  const buildNotes = () => {
    const channels = (needs.channels || []).join(', ');
    const hasRange = Number.isFinite(needs.budgetMin) && Number.isFinite(needs.budgetMax);
    const rangeTxt = hasRange ? `Budget: ${Intl.NumberFormat('fr-FR').format(needs.budgetMin)}€–${Intl.NumberFormat('fr-FR').format(needs.budgetMax)}€` : '';
    const typeTxt = hasRange ? (needs.budgetType === 'monthly' ? ' (mensuel)' : ' (ponctuel)') : '';
    const fallback = needs.budget ? `Budget: ${needs.budget}` : '';
    const budget = (rangeTxt || fallback) ? `${(rangeTxt || fallback)}${typeTxt}` : '';
    const obj = needs.objectives ? `Objectifs: ${needs.objectives}` : '';
    const extra = notes?.trim() ? `\nNotes: ${notes.trim()}` : '';
    return [channels, budget, obj].filter(Boolean).join(' | ') + extra;
  };

  const createProjectForCustomer = async (customerId) => {
    try {
      if (!customerId) return;
      const name = (needs.projectName || '').trim();
      const objectives = (needs.objectives || '').trim();
      const ACTION_MAP = {
        'Site Web': 'Site web',
        'SEO': 'SEO',
        'SEA/Ads': 'SEA',
        'Branding': 'Branding',
      };
      const allowed = ['Site web', 'E-commerce', 'SEO', 'SEA', 'Branding', 'Design', 'Développement', 'Maintenance', 'Formation', 'Licence', 'Autre'];
      const rawChannels = Array.isArray(needs.channels) ? needs.channels.filter(Boolean) : [];
      let mappedActions = rawChannels.map(c => ACTION_MAP[c] || null).filter(Boolean);
      const hadUnmapped = rawChannels.some(c => !ACTION_MAP[c]);
      if (hadUnmapped && !mappedActions.includes('Autre')) mappedActions.push('Autre');
      mappedActions = mappedActions.filter(a => allowed.includes(a));
      const budgetMin = Number.isFinite(needs.budgetMin) ? Number(needs.budgetMin) : undefined;
      const budgetMax = Number.isFinite(needs.budgetMax) ? Number(needs.budgetMax) : undefined;
      const payload = {
        name: name || 'Projet',
        objectives: objectives || undefined,
        actions: mappedActions.length ? mappedActions : undefined,
        budget_min: typeof budgetMin === 'number' ? budgetMin : undefined,
        budget_max: typeof budgetMax === 'number' ? budgetMax : undefined,
        description: notes || undefined,
        status: 'draft',
        client: customerId,
        customer_id: customerId
      };
      try {
        const year = new Date().getFullYear();
        const rnd = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
        payload.internal_code = `PRJ-${year}-${rnd}`;
      } catch { }
      await pb.collection('projects').create(payload);
    } catch (e) {
      console.warn('Onboarding project creation failed', e?.message || e);
    }
  };

  useEffect(() => {
    if (step === 1 || step === 2) {
      const t = setTimeout(saveCustomerDraft, 500);
      return () => clearTimeout(t);
    }
  }, [status, client.firstName, client.lastName, client.company, client.email, client.phone, client.address, client.postalCode, client.city, step, needs.channels, needs.budget, needs.objectives, notes]);

  const finish = async () => {
    try {
      await saveCustomerDraft();
      if (client.email && client.password) {
        let userId = null;
        try {
          const newUser = await pb.collection('users').create({
            email: client.email,
            password: client.password,
            passwordConfirm: client.confirm,
            name: fullName(client.firstName, client.lastName),
            emailVisibility: true,
            role: 'client'
          });
          userId = newUser.id;
        } catch (e) {
          try {
            const existing = await pb.collection('users').getFirstListItem(`email="${client.email}"`);
            if (existing) userId = existing.id;
          } catch { }
        }

        if (userId && draftCustomerId) {
          try {
            await pb.collection('customers').update(draftCustomerId, { user: userId });
          } catch (err) {
            console.error("Failed to link user to customer", err);
          }
        }

        try {
          pb.authStore.clear();
          await pb.collection('users').authWithPassword(client.email, client.password);
        } catch (err) {
          console.warn("Auto-login failed:", err);
        }
      }
      try { if (draftCustomerId) { await createProjectForCustomer(draftCustomerId); } } catch { }
      localStorage.removeItem('onb_customer_id');

      try {
        const audio = new Audio('https://assets.mixkit.co/sfx/preview/mixkit-success-fanfare-trumpets-613.mp3');
        audio.volume = 0.5;
        audio.play().catch(() => { });
      } catch { }
      setSuccess(true);
    } catch (e) {
      alert("Erreur lors de la finalisation. Réessayez plus tard.");
    }
  };



  return (
    <div className="onboarding-container">
      <div className="onboarding-inner">
        <div className="onboarding-head enhanced">
          <div className="head-top">
            <div className="onboarding-brand">
              <div className="brand-title">INFOCOLAB — Digital Studio</div>
            </div>
            <div className="head-actions">
              <button
                type="button"
                className="theme-toggle-btn"
                onClick={toggleTheme}
                aria-label={isDark ? 'Passer en mode clair' : 'Passer en mode sombre'}
                title={isDark ? 'Mode clair' : 'Mode sombre'}
              >
                {isDark ? '☀️' : '🌙'}
              </button>
              <a className="head-link" href="/">Retour au site</a>
            </div>
          </div>
          <div className="head-welcome">
            <h1>Propulsez votre futur</h1>
            <p>
              Créez votre compte client INFOCOLAB en quelques secondes et accédez à nos services exclusifs.
            </p>
          </div>
        </div>

        <Stepper
          step={step}
          total={total}
          onStepClick={async (target) => {
            try { await saveCustomerDraft(); } catch { }
            setStep(() => Math.min(total, Math.max(1, target)));
          }}
        />
        <div className="wizard-actions top">
          <div>
            {step > 1 && (
              <button className="link-action link-muted" type="button" onClick={prev}>Retour</button>
            )}
          </div>
          <div>
            {step < total && (
              <button className="link-action link-primary" type="button" onClick={next} disabled={!canNext && step !== 2}>Étape suivante</button>
            )}
            {step === total && (
              <button className="link-action link-success" type="button" onClick={finish}>Valider</button>
            )}
          </div>
        </div>

        <div className="onboarding-card">
          {step === 1 && (
            <div>
              <h2 style={{ margin: '0 0 10px' }}>Qui êtes-vous ?</h2>
              <div className="type-cards">
                {['entreprise', 'association', 'particulier'].map((s) => {
                  const primary = '#2563eb';
                  const border = status === s ? primary : (isDark ? '#253041' : '#e5e7eb');
                  const cardBg = isDark ? '#0f172a' : '#ffffff';
                  const textColor = isDark ? '#e5e7eb' : '#111827';
                  const iconBg = status === s
                    ? (isDark ? 'linear-gradient(180deg,#1e293b,#1d4ed8)' : 'linear-gradient(180deg,#dbeafe,#bfdbfe)')
                    : (isDark ? 'linear-gradient(180deg,#111827,#1f2937)' : 'linear-gradient(180deg,#f3f4f6,#eef2ff)');
                  const boxShadow = status === s
                    ? (isDark ? '0 8px 22px rgba(37,99,235,0.18)' : '0 8px 22px rgba(37,99,235,0.12)')
                    : 'none';
                  return (
                    <button
                      key={s}
                      type="button"
                      className={`type-card ${status === s ? 'active' : ''}`}
                      onClick={() => { setStatus(s); next(); }}
                    >
                      <div className="type-icon" aria-hidden="true">{(ICONS[s] || (() => null))()}</div>
                      <div className="type-label">{s}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 style={{ margin: '0 0 10px' }}>Vos informations</h2>
              <div style={{ display: 'grid', gap: 12, gridTemplateColumns: '1fr' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label>Prénom *</label>
                    <input className={errors.firstName ? 'input-error' : ''} type="text" name="firstName" autoComplete="given-name" value={client.firstName} onChange={(e) => setClient({ ...client, firstName: e.target.value })} placeholder="Jean" />
                    {errors.firstName && <div className="field-error">{errors.firstName}</div>}
                  </div>
                  <div>
                    <label>Nom *</label>
                    <input className={errors.lastName ? 'input-error' : ''} type="text" name="lastName" autoComplete="family-name" value={client.lastName} onChange={(e) => setClient({ ...client, lastName: e.target.value })} placeholder="Dupont" />
                    {errors.lastName && <div className="field-error">{errors.lastName}</div>}
                  </div>
                </div>
                {status !== 'particulier' && (
                  <div>
                    <label>Entreprise</label>
                    <input type="text" name="company" autoComplete="organization" value={client.company} onChange={(e) => setClient({ ...client, company: e.target.value })} placeholder="Infocolab" />
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label>Email *</label>
                    <input className={errors.email ? 'input-error' : ''} type="email" name="email" autoComplete="email" value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} placeholder="jean@email.com" />
                    {errors.email && <div className="field-error">{errors.email}</div>}
                  </div>
                  <div>
                    <label>Téléphone *</label>
                    <input className={errors.phone ? 'input-error' : ''} type="tel" name="phone" autoComplete="tel" value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} placeholder="06 12 34 56 78" />
                    {errors.phone && <div className="field-error">{errors.phone}</div>}
                  </div>
                </div>
                <div>
                  <label>Adresse *</label>
                  <input className={errors.address ? 'input-error' : ''} type="text" name="address" autoComplete="street-address" value={client.address} onChange={(e) => setClient({ ...client, address: e.target.value })} placeholder="123 rue de Paris" />
                  {errors.address && <div className="field-error">{errors.address}</div>}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label>Code postal *</label>
                    <input className={errors.postalCode ? 'input-error' : ''} type="text" name="postalCode" autoComplete="postal-code" value={client.postalCode} onChange={(e) => setClient({ ...client, postalCode: e.target.value })} placeholder="75000" />
                    {errors.postalCode && <div className="field-error">{errors.postalCode}</div>}
                  </div>
                  <div>
                    <label>Ville *</label>
                    <input className={errors.city ? 'input-error' : ''} type="text" name="city" autoComplete="address-level2" value={client.city} onChange={(e) => setClient({ ...client, city: e.target.value })} placeholder="Paris" />
                    {errors.city && <div className="field-error">{errors.city}</div>}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label>Mot de passe *</label>
                    <input className={errors.password ? 'input-error' : ''} type="password" name="password" autoComplete="new-password" value={client.password} onChange={(e) => setClient({ ...client, password: e.target.value })} placeholder="••••••••" />
                    {errors.password && <div className="field-error">{errors.password}</div>}
                  </div>
                  <div>
                    <label>Confirmer *</label>
                    <input className={errors.confirm ? 'input-error' : ''} type="password" name="confirm" autoComplete="new-password" value={client.confirm} onChange={(e) => setClient({ ...client, confirm: e.target.value })} placeholder="••••••••" />
                    {errors.confirm && <div className="field-error">{errors.confirm}</div>}
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 style={{ margin: '0 0 10px' }}>Vos besoins</h2>
              <div style={{ display: 'grid', gap: 12 }}>
                <div>
                  <label>Choisissez vos actions marketing</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {['Site Web', 'SEO', 'SEA/Ads', 'Réseaux sociaux', 'Emailing', 'Branding', 'Contenu', 'Application mobile', 'Analytics & Data', 'Marketplace', 'Autre'].map((c) => {
                      const active = needs.channels.includes(c);
                      return (
                        <button
                          type="button"
                          key={c}
                          data-tip={CHANNEL_TIPS[c] || ''}
                          onClick={() => setNeeds((n) => ({ ...n, channels: active ? n.channels.filter(x => x !== c) : [...n.channels, c] }))}
                          className={`channel-pill ${active ? 'active' : ''} tooltip`}
                        >
                          {c}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label>Nom du projet</label>
                    <input type="text" value={needs.projectName} onChange={(e) => setNeeds({ ...needs, projectName: e.target.value })} placeholder="ex: Site vitrine, refonte logo..." />
                  </div>
                  <div>
                    <label>Objectifs</label>
                    <input type="text" value={needs.objectives} onChange={(e) => setNeeds({ ...needs, objectives: e.target.value })} placeholder="ex: leads, notoriété..." />
                  </div>
                </div>
                <div style={{ marginTop: 10 }}>
                  <label>Budget indicatif</label>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700 }}>
                      {Intl.NumberFormat('fr-FR').format(needs.budgetMin)}€ – {Intl.NumberFormat('fr-FR').format(needs.budgetMax)}€ {needs.budgetType === 'monthly' ? '/ mois' : ''}
                    </div>
                    <div style={{ display: 'inline-flex', background: 'var(--surface)', border: '1px solid var(--muted-border)', borderRadius: 10, overflow: 'hidden' }}>
                      <button type="button" className={`btn ${needs.budgetType === 'one_time' ? 'primary' : ''}`} style={{ border: 'none', borderRadius: 0 }} onClick={() => setNeeds(n => ({ ...n, budgetType: 'one_time' }))}>Une fois</button>
                      <button type="button" className={`btn ${needs.budgetType === 'monthly' ? 'primary' : ''}`} style={{ border: 'none', borderRadius: 0 }} onClick={() => setNeeds(n => ({ ...n, budgetType: 'monthly' }))}>Mensuel</button>
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 8 }}>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--muted)' }}>Min (€)</label>
                      <input type="number" min={0} max={100000} step={50} value={needs.budgetMin}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(100000, Number(e.target.value)));
                          setNeeds(n => ({ ...n, budgetMin: Math.min(v, n.budgetMax) }));
                        }} />
                    </div>
                    <div>
                      <label style={{ fontSize: 12, color: 'var(--muted)' }}>Max (€)</label>
                      <input type="number" min={0} max={100000} step={50} value={needs.budgetMax}
                        onChange={(e) => {
                          const v = Math.max(0, Math.min(100000, Number(e.target.value)));
                          setNeeds(n => ({ ...n, budgetMax: Math.max(v, n.budgetMin) }));
                        }} />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 6, marginTop: 8 }}>
                    <input type="range" min={0} max={100000} step={50} value={needs.budgetMin}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setNeeds(n => ({ ...n, budgetMin: Math.min(v, n.budgetMax) }));
                      }} />
                    <input type="range" min={0} max={100000} step={50} value={needs.budgetMax}
                      onChange={(e) => {
                        const v = Number(e.target.value);
                        setNeeds(n => ({ ...n, budgetMax: Math.max(v, n.budgetMin) }));
                      }} />
                  </div>
                </div>
                <div>
                  <label>Notes complémentaires</label>
                  <textarea rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Ajoutez des précisions utiles..." />
                </div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div className="recap-container">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '30px' }}>
                <h2 style={{ margin: 0 }}>Récapitulatif de votre projet</h2>
                <div style={{ display: 'flex', gap: 10 }}>
                  {!recapEditMode ? (
                    <button className="btn" onClick={() => setRecapEditMode(true)}>Modifier tout</button>
                  ) : (
                    <>
                      <button className="btn primary" onClick={() => { saveEdit(); setRecapEditMode(false); }}>Terminer</button>
                      <button className="btn" onClick={() => { cancelEdit(); setRecapEditMode(false); }}>Annuler</button>
                    </>
                  )}
                </div>
              </div>

              <div className="recap-grid">
                {/* Section 1: Profil */}
                <div className="recap-section">
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>
                    Votre Profil
                  </h3>
                  <div className="recap-item">
                    <span className="recap-label">TYPE DE COMPTE</span>
                    <span className="recap-value"><span className="recap-badge">{status}</span></span>
                  </div>
                  <div className="recap-item">
                    <span className="recap-label">NOM COMPLET</span>
                    <span className="recap-value">
                      {editField === 'name' ? (
                        <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus onBlur={saveEdit} onKeyDown={e => e.key === 'Enter' && saveEdit()} />
                      ) : (
                        <>
                          {fullName(client.firstName, client.lastName)}
                          {recapEditMode && <button className="recap-edit-btn" onClick={() => startEdit('name', fullName(client.firstName, client.lastName))}>✏️</button>}
                        </>
                      )}
                    </span>
                  </div>
                  {status !== 'particulier' && (
                    <div className="recap-item">
                      <span className="recap-label">ENTREPRISE</span>
                      <span className="recap-value">
                        {editField === 'company' ? (
                          <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus onBlur={saveEdit} />
                        ) : (
                          <>
                            {client.company || '—'}
                            {recapEditMode && <button className="recap-edit-btn" onClick={() => startEdit('company', client.company)}>✏️</button>}
                          </>
                        )}
                      </span>
                    </div>
                  )}
                  <div className="recap-item">
                    <span className="recap-label">CONTACT</span>
                    <span className="recap-value" style={{ fontSize: '0.9rem' }}>{client.email}</span>
                    <span className="recap-value" style={{ fontSize: '0.9rem' }}>{client.phone}</span>
                  </div>
                </div>

                {/* Section 2: Détails du Projet */}
                <div className="recap-section">
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
                    Votre Projet
                  </h3>
                  <div className="recap-item">
                    <span className="recap-label">NOM DU PROJET</span>
                    <span className="recap-value">
                      {editField === 'projectName' ? (
                        <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus onBlur={saveEdit} />
                      ) : (
                        <>
                          {needs.projectName || 'Non spécifié'}
                          {recapEditMode && <button className="recap-edit-btn" onClick={() => startEdit('projectName', needs.projectName)}>✏️</button>}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="recap-item">
                    <span className="recap-label">CANAUX SÉLECTIONNÉS</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                      {needs.channels.length > 0 ? (
                        needs.channels.map(c => <span key={c} className="recap-badge" style={{ background: 'var(--glass-accent)', color: 'var(--primary)', boxShadow: 'none' }}>{c}</span>)
                      ) : (
                        <span className="recap-value">—</span>
                      )}
                    </div>
                  </div>
                  <div className="recap-item">
                    <span className="recap-label">OBJECTIFS</span>
                    <span className="recap-value">
                      {editField === 'objectives' ? (
                        <input value={editValue} onChange={e => setEditValue(e.target.value)} autoFocus onBlur={saveEdit} />
                      ) : (
                        <>
                          {needs.objectives || '—'}
                          {recapEditMode && <button className="recap-edit-btn" onClick={() => startEdit('objectives', needs.objectives)}>✏️</button>}
                        </>
                      )}
                    </span>
                  </div>
                </div>

                {/* Section 3: Investissement (Full width or specific) */}
                <div className="recap-section" style={{ gridColumn: '1 / -1', background: 'var(--glass-accent)', borderColor: 'var(--primary)' }}>
                  <h3>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>
                    Investissement Prévu
                  </h3>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <span className="recap-label">BUDGET ESTIMÉ</span>
                      <div className="recap-value" style={{ fontSize: '1.8rem', color: 'var(--primary)', marginTop: '5px' }}>
                        {Intl.NumberFormat('fr-FR').format(needs.budgetMin)}€ – {Intl.NumberFormat('fr-FR').format(needs.budgetMax)}€
                        <span style={{ fontSize: '1rem', opacity: 0.7, marginLeft: '10px' }}>
                          {needs.budgetType === 'monthly' ? '/ mois' : 'ponctuel'}
                        </span>
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <span className="recap-label">STATUS</span>
                      <div className="recap-value" style={{ color: 'var(--success)' }}>
                        ● Prêt à être lancé
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div style={{ fontSize: 12, color: 'var(--muted)', margin: '8px 2px 0' }}>
          Seuls les champs avec * sont obligatoires. Vous pourrez modifier vos informations ensuite.
        </div>

        <div className="wizard-actions bottom">
          <div>
            {step > 1 && (
              <button className="btn" type="button" onClick={prev}>Retour</button>
            )}
          </div>
          <div>
            {step < total && (
              <button className="btn primary" type="button" onClick={next} disabled={!canNext && step !== 2}>Suivant</button>
            )}
            {step === total && (
              <button className="btn success" type="button" onClick={finish}>Valider</button>
            )}
          </div>
        </div>

        <div className="onboarding-footer">
          <div className="footer-inner">
            <div className="footer-help">
              <a className="footer-help-title" href="mailto:contact@infocolab.com" aria-label="Besoin d’aide ? Écrire un email">Besoin d’aide ?</a>
              <a className="footer-link" href="mailto:contact@infocolab.com" aria-label="Envoyer un email">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16v16H4z" /><path d="m22 6-10 7L2 6" /></svg>
                contact@infocolab.com
              </a>
              <a className="footer-link" href="tel:+33769297963" aria-label="Appeler">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.09 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.12.81.3 1.6.57 2.35a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.73-1.09a2 2 0 0 1 2.11-.45c.75.27 1.54.45 2.35.57A2 2 0 0 1 22 16.92z" /></svg>
                07 69 29 79 63
              </a>
            </div>
            <div className="footer-meta">© {new Date().getFullYear()} Infocolab — <a href="/mentions-legales">Mentions légales</a></div>
          </div>
        </div>
      </div>

      {success && (
        <div className="success-overlay">
          <div className="success-card">
            <div className="success-icon-wrap">
              <div className="success-icon-bg">
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
              </div>
            </div>

            <h1>C'est fait !</h1>
            <p className="success-greeting">Félicitations {client.firstName}, votre compte est prêt.</p>
            <p className="success-message">
              Bienvenue dans l'aventure InfoColab. Votre projet a été enregistré et votre espace client est maintenant accessible.
            </p>

            <div className="success-actions">
              <button onClick={() => navigate('/mon-compte')} className="btn primary btn-large">
                Accéder à mon espace client
              </button>
              <p className="auto-redirect">Redirection automatique dans quelques secondes...</p>
            </div>

            <button onClick={() => navigate('/mon-compte')} className="success-close">✕</button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { transform: scale(0); opacity: 0; } to { transform: scale(1); opacity: 1; } }
      `}</style>
    </div>
  );
}
